import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type BomItem, type FileInfo } from "@/client";
import { HttpError } from "@/http-error";
import {
  AlertCircle,
  ImageOff,
  Loader2,
} from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogHeader,
  DialogDescription,
} from "./ui/dialog";
import { Maximize } from "lucide-react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { AddToCartButton } from "./add-to-cart-button";
import { localizedItemName } from "@/lib/app-language";
import { useAppLanguage } from "@/hooks/use-app-language";

export function Preview({
  previewFileInfo,
  isLoading,
  error,
  selectedItem,
}: {
  previewFileInfo: FileInfo | null | undefined;
  isLoading: boolean;
  error: unknown;
  selectedItem: BomItem | null;
}) {
  const { t } = useTranslation();
  const language = useAppLanguage();
  const itemName = useMemo(() => {
    if (!selectedItem) return t("bom.previewCardTitle");
    return localizedItemName(language, selectedItem.nameDe, selectedItem.nameEn);
  }, [selectedItem, language, t]);

  if (isLoading) {
    return <Loading />;
  }

  if (
    !previewFileInfo ||
    (error instanceof HttpError && error.status === 404)
  ) {
    return <NotFound />;
  }

  if (error) {
    const message =
      error instanceof HttpError && error.status === 408
        ? t("error.http.408")
        : error instanceof HttpError
          ? error.message
          : t("error.unexpected");
    return <Error message={message} />;
  }

  return <PreviewImage imageUrl={previewFileInfo.url} itemName={itemName} />;
}

export function PreviewImage({
  imageUrl,
  itemName,
}: {
  imageUrl: string;
  itemName: string;
}) {
  const [hasError, setHasError] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setHasError(false);
  }, [imageUrl]);

  if (hasError) {
    return (
      <div className="flex items-center justify-center w-full h-48 bg-gray-100 rounded-lg">
        <ImageOff className="w-12 h-12 text-gray-400" />
      </div>
    );
  }
  if (isMobile) {
        return (
      <img
        src={imageUrl}
        alt={itemName}
        className="max-w-full max-h-full object-contain rounded-lg"
        onError={() => setHasError(true)}
      />
    );
  }
  return (
    <>
      <ImageDialog
        imageUrl={imageUrl}
        itemName={itemName}
        onError={ () => setHasError(true)}
      />
    </>
  );
}

interface MobilePreviewDrawerProps {
  isDrawerOpen: boolean;
  onDrawerOpenChange: (open: boolean) => void;
  selectedItem: BomItem | null;
  onAddToCart: () => void;
  canBeAddedToCart: boolean;
  previewFileInfo: FileInfo | null | undefined;
  isLoading: boolean;
  error: unknown;
}

export function MobilePreviewDrawer({
  isDrawerOpen,
  onDrawerOpenChange,
  selectedItem,
  onAddToCart,
  canBeAddedToCart,
  previewFileInfo,
  isLoading,
  error,
}: MobilePreviewDrawerProps) {
  const { t } = useTranslation();
  const language = useAppLanguage();

  const itemName = useMemo(() => {
    if (!selectedItem) return t("bom.previewCardTitle");
    return localizedItemName(language, selectedItem.nameDe, selectedItem.nameEn);
  }, [selectedItem, language, t]);

  return (
    <div className="lg:hidden">
      <Drawer open={isDrawerOpen} onOpenChange={onDrawerOpenChange}>
        <DrawerContent className="h-[80vh]">
          <DrawerHeader className="flex-row justify-between pb-2">
            <DrawerTitle className="text-xl font-bold">{itemName}</DrawerTitle>
            <div className="relative">
              <AddToCartButton
                onAddToCart={onAddToCart}
                canBeAddedToCart={canBeAddedToCart}
              />
            </div>
          </DrawerHeader>
          <div className="flex-1 overflow-auto flex flex-col justify-center items-center">
              <Preview
                previewFileInfo={previewFileInfo}
                isLoading={isLoading}
                error={error}
                selectedItem={selectedItem}
              />
          </div>
          <DrawerDescription className="sr-only">{itemName}</DrawerDescription>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

export function ImageDrawer({
  imageUrl,
  itemName,
}: {
  imageUrl: string;
  itemName: string;
  onError?: () => void;
}) {
  const [t] = useTranslation();

  // Don't render image if imageUrl is empty or undefined
  if (!imageUrl || imageUrl.trim() === "") {
    return (
      <div className="relative w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
        <ImageOff className="w-8 h-8 text-gray-400" />
      </div>
    );
  }

  return (
    <Drawer
    >
      <DrawerTrigger asChild>
        <div className="relative w-full h-full flex items-center justify-center cursor-pointer">
          <img
            src={imageUrl}
            alt={itemName}
            className="max-w-full max-h-full object-contain rounded-lg transition-transform"
          />
          <div className="absolute top-2 right-2 p-2 bg-black bg-opacity-50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity">
            <Maximize className="w-5 h-5" />
          </div>
        </div>
      </DrawerTrigger>
      <DrawerContent className="h-[90%] lg:h-[90%] w-full max-w-4xl mx-auto flex flex-col p-4" >
        <DrawerHeader className="pb-2">
          <DrawerTitle className="text-xl font-bold">{itemName}</DrawerTitle>
        </DrawerHeader>
        <div className="flex-1 overflow-hidden flex items-center justify-center">
          <img
            src={imageUrl}
            alt={t("bom.enlargedImageAlt", { itemName })}
            className="max-w-full max-h-full object-contain rounded-lg"
            />
        </div>
            <DrawerDescription className="sr-only">
              {itemName}
            </DrawerDescription>
      </DrawerContent>
    </Drawer>
  );
}


export function ImageDialog({
  imageUrl,
  itemName,
  onError,
}: {
  imageUrl: string;
  itemName: string;
  onError?: () => void;
}) {
  const [t] = useTranslation();

  // Don't render image if imageUrl is empty or undefined
  if (!imageUrl || imageUrl.trim() === "") {
    return (
      <div className="relative w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
        <ImageOff className="w-8 h-8 text-gray-400" />
      </div>
    );
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div className="relative w-full h-full flex items-center justify-center cursor-pointer">
          <img
            src={imageUrl}
            alt={itemName}
            className="max-w-full max-h-full object-contain rounded-lg transition-transform"
            onError={onError}
          />
        </div>
      </DialogTrigger>
      <DialogContent className="w-screen h-screen rounded-none lg:w-[90vw] lg:h-[90vh] lg:max-w-none flex flex-col p-4" >
        <DialogHeader className="pb-2">
          <DialogTitle className="text-xl font-bold">{itemName}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden flex items-center justify-center">
          <img
            src={imageUrl}
            alt={t("bom.enlargedImageAlt", { itemName })}
            className="max-w-full max-h-full object-contain rounded-lg"
            onError={onError}
            />
        </div>
            <DialogDescription className="sr-only">
              {itemName}
            </DialogDescription>
      </DialogContent>
    </Dialog>
  );
}

function NotFound() {
  const [t] = useTranslation();
  return (
    <div className="p-6 flex flex-col items-center justify-center text-gray-500 h-full">
      <ImageOff className="h-12 w-12 mb-2" />
      <p className="text-sm">{t("bom.previewNotFound")}</p>
    </div>
  );
}

function Loading() {
  const [t] = useTranslation();
  return (
    <div className="p-6 flex flex-col items-center justify-center h-full">
      <Loader2 className="h-12 w-12 animate-spin text-primary" />
      <p className="mt-2 text-sm text-gray-500">{t("bom.loadingPreview")}</p>
    </div>
  );
}

function Error({ message }: { message: string }) {
  const [t] = useTranslation();
  return (
    <div className="p-6 flex flex-col items-center justify-center text-red-600 bg-red-50 rounded-lg h-full">
      <AlertCircle className="h-12 w-12 mb-2" />
      <p className="text-sm font-medium">{t("common.error")}</p>
      <p className="text-sm">{message || t("bom.previewError")}</p>
    </div>
  );
}
